import type { ComputedRef } from 'vue'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type {
  TutorAttendanceRate,
  TutorAttendanceRateBooking,
  TutorAttendanceRateCustomer,
  TutorAttendanceRateTutor,
} from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { getCustomerUrl, getKidUrl } from '@shared/lib/routes'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'

import { getLessonUrl, getTutorUrl } from './tutor-attendance-rate-links'
import { formatAttendanceScore } from '../lib/attendance-score'
import { useTutorAttendanceRatesStore } from './store'

const EMPTY_CELL = '-'

interface TutorAttendanceRatesTotalsView {
  maxScore: string
  actualScore: string
}

interface TableTutorAttendanceRatesReturn {
  rates: ComputedRef<TutorAttendanceRate[]>
  isLoading: ComputedRef<boolean>
  hasRates: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  totals: ComputedRef<TutorAttendanceRatesTotalsView | null>
  emptyCell: string
  getLessonUrl: (booking: TutorAttendanceRateBooking) => string
  getStart: (rate: TutorAttendanceRate) => string
  getTutorName: (tutor: TutorAttendanceRateTutor) => string
  getTutorUrl: (tutorId: number) => string
  getCustomerName: (customer: TutorAttendanceRateCustomer) => string
  getCustomerUrl: (customerId: number) => string
  getKidUrl: (kidId: number) => string
  getScore: (rate: TutorAttendanceRate) => string
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

const getFullName = (person: { first_name: string, last_name: string }): string =>
  `${person.first_name} ${person.last_name}`.trim()

export const useTableTutorAttendanceRates = (): TableTutorAttendanceRatesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useTutorAttendanceRatesStore()
  const dayjs = useDayjs()

  const rates = computed<TutorAttendanceRate[]>(() => store.getRates)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasRates = computed<boolean>(() => store.hasRates)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  // Every event is worth at most one point, so the event count is the maximum score.
  const totals = computed<TutorAttendanceRatesTotalsView | null>(() => {
    const storeTotals = store.getTotals
    if (!storeTotals) return null

    return {
      maxScore: String(storeTotals.count),
      actualScore: formatAttendanceScore(storeTotals.sum),
    }
  })

  const getStart = (rate: TutorAttendanceRate): string => dayjs(rate.booking.start).format(DateFormat.KanbanDate)

  const getScore = (rate: TutorAttendanceRate): string => formatAttendanceScore(rate.value)

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  return {
    rates,
    isLoading,
    hasRates,
    meta,
    perPageOptions,
    totals,
    emptyCell: EMPTY_CELL,
    getLessonUrl,
    getStart,
    getTutorName: getFullName,
    getTutorUrl,
    getCustomerName: getFullName,
    getCustomerUrl,
    getKidUrl,
    getScore,
    setChangePage,
    setChangePerPage,
  }
}
